import React from 'react';
import styled from 'styled-components';
import Hero from '../components/Hero';
import SectionTitle from '../components/SectionTitle';
import ServiceList from '../components/ServiceList';
import TestimonialSlider from '../components/TestimonialSlider';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';
import { heroImage, aboutImage } from '../assets/images';
import { FaAward, FaUsers, FaMusic, FaMapMarkerAlt } from 'react-icons/fa';

const Home = () => {
  return (
    <main>
      <SEO 
        title="TK DJ Services | Premier DJ, Audio, and Lighting Services in the DMV"
        description="Professional DJ, audio engineering, and lighting production services with over 20 years of experience. Serving weddings, corporate events, and private parties in the DMV area."
        url="/"
        canonical="/"
        image="/images/social-share.jpg"
      />
      <Hero 
        backgroundImage={heroImage}
        title="Premier DJ & Audio Services in the DMV"
        subtitle="With over 20 years of experience, TK DJ Services provides top-tier entertainment and technical expertise for events, venues, and audio enthusiasts."
        buttonText="Learn More"
        buttonLink="#about"
      />
      
      <AboutSection id="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <SectionTitle 
                title="About TK DJ Services" 
                subtitle="TK DJ Services is a full-service professional DJ, audio, and lighting company serving the Washington Metropolitan area (DMV)."
              />
              <p>
                With over two decades of experience in DJing, audio engineering, and lighting design, we provide unparalleled entertainment and technical expertise for nightclubs, private parties, corporate events, and weddings.
              </p>
              <p>
                Beyond DJ services, we specialize in professional audio equipment repair, audio and lighting engineering for live events, and full-scale installation of audio and lighting systems. Our custom-crafted speaker fabrication ensures that every venue gets a high-quality, tailor-made sound experience.
              </p>
              <AboutStats>
                <StatItem>
                  <StatIcon>
                    <FaAward />
                  </StatIcon>
                  <StatNumber>20+</StatNumber>
                  <StatText>Years Experience</StatText>
                </StatItem>
                <StatItem>
                  <StatIcon>
                    <FaMapMarkerAlt />
                  </StatIcon>
                  <StatNumber>DMV</StatNumber>
                  <StatText>Area Coverage</StatText>
                </StatItem>
                <StatItem>
                  <StatIcon>
                    <FaMusic />
                  </StatIcon>
                  <StatNumber>1000+</StatNumber>
                  <StatText>Events</StatText>
                </StatItem>
              </AboutStats>
            </div>
            <AboutImage style={{ backgroundImage: `url(${aboutImage})` }}>
              <AboutImageOverlay />
            </AboutImage>
          </div>
        </div>
      </AboutSection>
      
      <ServicesSection id="services">
        <div className="container">
          <SectionTitle 
            title="Our Services" 
            subtitle="From professional DJ services to custom audio solutions, we offer a comprehensive range of services to meet your entertainment and technical needs."
            center
          />
          <ServiceList />
        </div>
      </ServicesSection>
      
      <TestimonialsSection>
        <div className="container">
          <SectionTitle 
            title="What Our Clients Say" 
            subtitle="Don't just take our word for it. Here's what our clients have to say about our services."
            center
          />
          <TestimonialSlider />
        </div>
      </TestimonialsSection>
      
      <ContactSection id="contact">
        <div className="container">
          <SectionTitle 
            title="Get In Touch" 
            subtitle="Ready to elevate your event or venue? Contact us today to discuss how we can help."
            center
          />
          <ContactForm />
        </div>
      </ContactSection>
    </main>
  );
};

const AboutSection = styled.section`
  padding: 8rem 0 5rem;
  
  .about-content {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 3rem;
    align-items: center;
    
    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }
  
  .about-text {
    p {
      margin-bottom: 1.5rem;
      line-height: 1.8;
    }
  }
`;

const AboutStats = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 3rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatIcon = styled.div`
  font-size: 2rem;
  color: var(--color-secondary);
  margin-bottom: 0.5rem;
`;

const StatNumber = styled.h3`
  font-size: 2.5rem;
  color: var(--color-tertiary);
  margin-bottom: 0.5rem;
`;

const StatText = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  font-size: 0.9rem;
`;

const AboutImage = styled.div`
  position: relative;
  height: 450px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 992px) {
    height: 400px;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const AboutImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(19, 19, 19, 0.2) 0%,
    rgba(19, 19, 19, 0.4) 100%
  );
`;

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const TestimonialsSection = styled.section`
  padding: 5rem 0;
`;

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

export default Home; 